import React from 'react';
import { ClipboardListIcon, PersonStandingIcon, SlidersHorizontalIcon, TrafficConeIcon, WrenchIcon } from 'lucide-react';
import { SectionHeading } from '../SectionHeading';
import { adjustments, ageGroups, gameExamples, lessons, quickFixes, stations } from '../../data/salesContent';
type CardHeaderProps = {
  icon: React.ElementType;
  iconClass: string;
  value?: string;
  title: string;
};
function CardHeader({
  icon: Icon,
  iconClass,
  value,
  title
}: CardHeaderProps) {
  return <div className="flex items-start gap-4" data-id="element-68" data-mp="1nzi5sn6t0ios">
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${iconClass}`} data-id="element-69" data-mp="1nyfisqclr0e5">
        <Icon className="h-6 w-6" aria-hidden="true" data-id="element-70" data-mp="1j16wqeoc2c7m" />
      </span>
      <h3 className="font-display text-xl font-extrabold uppercase leading-tight tracking-tight text-ink" data-id="element-71" data-mp="1e2vm41q7rxoc">
        {value && <span className="block text-4xl leading-none text-pumpkin-dark" data-id="element-72" data-mp="19yxppys9x0yz">{value}</span>}
        {title}
      </h3>
    </div>;
}
export function IncludedSection() {
  return <section className="bg-cream py-8 sm:py-12" aria-labelledby="included-title" data-id="element-73" data-mp="1lepk18ildwna">
      <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-74" data-mp="1q5o32fuyt8u8">
        <div id="included-title" data-id="element-75" data-mp="1q7iodva5f2yu">
          <SectionHeading tone="light" title="Everything You Need for Halloween PE Week" subtitle="Not random activities. A complete ready-to-use teaching system." data-id="element-76" data-mp="1cejce39krqw1" />
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-3" data-id="element-77" data-mp="1ge1xaz1ec1yv">
          {/* Games */}
          <article className="rounded-3xl bg-ink p-6 text-cream sm:p-8 lg:col-span-2" data-id="element-78" data-mp="2468m9d4sl6o9">
            <div className="flex items-start gap-4" data-id="element-79" data-mp="293imzoxmt4re">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-pumpkin text-ink" data-id="element-80" data-mp="1v5asdno2ifje">
                <PersonStandingIcon className="h-6 w-6" aria-hidden="true" data-id="element-81" data-mp="203lz3pj2g9xe" />
              </span>
              <h3 className="font-display text-xl font-extrabold uppercase leading-tight tracking-tight" data-id="element-82" data-mp="jpcdh367kza9">
                <span className="block text-5xl leading-none text-pumpkin" data-id="element-83" data-mp="op8hh2ewoqpt">25</span>
                Ready-to-Run Halloween Games
              </h3>
            </div>
            <p className="mt-4 text-[13px] leading-snug text-muted" data-id="element-84" data-mp="2lf1g2ggnt7aa">
              Agility, reaction, passing, throwing, teamwork, spatial awareness, coordination and movement skills.
            </p>
            <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3" data-id="element-85" data-mp="2cmpr6z171rwf">
              {gameExamples.map(g => <li key={g} className="flex items-center gap-2 text-[15px] text-cream" data-id="element-86" data-mp="27nd54gi5couw">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-lime" aria-hidden="true" data-id="element-87" data-mp="22ojq2jn7opsm" />
                  {g}
                </li>)}
              <li className="text-[15px] italic text-muted" data-id="element-88" data-mp="1xqi8k35foyj9">…and more.</li>
            </ul>
          </article>

          {/* Stations */}
          <article className="flex flex-col rounded-3xl bg-paper p-6 ring-1 ring-ink/10 sm:p-8" data-id="element-89" data-mp="1tpybt0orvwxn">
            <CardHeader icon={TrafficConeIcon} iconClass="bg-plum text-cream" value="10" title="Visual PE Stations" data-id="element-90" data-mp="3s1ds20i5py0t" />
            <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 lg:grid-cols-1" data-id="element-91" data-mp="3o01vfb0v8cj4">
              {stations.map(s => <li key={s} className="flex items-center gap-2 text-[15px] text-ink" data-id="element-92" data-mp="1et8bv7wv09zh">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-plum" aria-hidden="true" data-id="element-93" data-mp="1eqvje0th6h86" />
                  {s}
                </li>)}
            </ul>
            <p className="mt-auto pt-6 text-sm leading-relaxed text-ink/70" data-id="element-94" data-mp="1nkh19t0xselt">
              Each station clearly shows what students do and how the station is organized.
            </p>
          </article>

          {/* Lessons */}
          <article className="rounded-3xl bg-paper p-6 ring-1 ring-ink/10 sm:p-8" data-id="element-95" data-mp="vwgj87vfgiir">
            <CardHeader icon={ClipboardListIcon} iconClass="bg-lime text-ink" value="8" title="Complete Lesson Options" data-id="element-96" data-mp="zuearhco7gpb" />
            <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1.5 lg:grid-cols-1" data-id="element-97" data-mp="14sxzfpinf6ld">
              {lessons.map(l => <li key={l} className="text-[14px] leading-snug text-ink" data-id="element-98" data-mp="2bdgyxdmuqivz">
                  {l}
                </li>)}
            </ul>
          </article>

          {/* Adaptations */}
          <article className="rounded-3xl bg-paper p-6 ring-1 ring-ink/10 sm:p-8" data-id="element-99" data-mp="2etaeeiaoavc1">
            <CardHeader icon={SlidersHorizontalIcon} iconClass="bg-pumpkin text-ink" title="Age & Difficulty Adaptations" data-id="element-100" data-mp="29viasr0o1ks0" />
            <p className="mt-6 text-sm font-semibold text-ink/70" data-id="element-101" data-mp="2nrm2ack5nf0s">Use the same system with:</p>
            <ul className="mt-3 grid grid-cols-3 gap-2" data-id="element-102" data-mp="2ju7v0qg1bhbf">
              {ageGroups.map(a => <li key={a} className="flex h-12 items-center justify-center rounded-xl bg-ink px-2 text-center font-display text-[13px] font-bold leading-tight text-cream" data-id="element-103" data-mp="2xqbky7jvl3i3">
                  {a}
                </li>)}
            </ul>
            <p className="mt-6 text-sm font-semibold text-ink/70" data-id="element-104" data-mp="37jin7jjbk3dy">Adjust:</p>
            <ul className="mt-3 flex flex-wrap gap-2" data-id="element-105" data-mp="32jeggl87qn0x">
              {adjustments.map(a => <li key={a} className="rounded-full bg-ink/[0.07] px-3 py-1.5 text-[13px] font-medium text-ink" data-id="element-106" data-mp="3gj5qkavlzj9n">
                  {a}
                </li>)}
            </ul>
          </article>

          {/* Quick fixes */}
          <article className="flex flex-col rounded-3xl bg-paper p-6 ring-1 ring-ink/10 sm:p-8" data-id="element-107" data-mp="3v5w01zjhki6b">
            <CardHeader icon={WrenchIcon} iconClass="bg-plum text-cream" title="Quick Fixes for Real Class Problems" data-id="element-108" data-mp="3y0buyuhr0r5" />
            <p className="mt-6 text-sm font-semibold text-ink/70" data-id="element-109" data-mp="3m6r2owjnuuvq">What if:</p>
            <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 lg:grid-cols-1" data-id="element-110" data-mp="3q46qkobwwckc">
              {quickFixes.map(q => <li key={q} className="text-[14px] leading-snug text-ink" data-id="element-111" data-mp="3cciegl1nxo3d">
                  {q}
                </li>)}
            </ul>
            <p className="mt-auto pt-4 text-sm leading-relaxed text-ink/70" data-id="element-112" data-mp="32gp5tcr2eeqv">
              The guide gives you practical adjustments instead of forcing you to replace the whole activity.
            </p>
          </article>
        </div>
      </div>
    </section>;
}